/**
 * WHAT: SQL job management for the Custom SQL Executor module.
 * WHY: Enables DBAs to define, schedule, and execute SQL-based data collection jobs
 *      against multiple database connections, automating repetitive UAR data extraction.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: Execution job CRUD and management with scheduling support.
 * WHY: DBAs need to configure reusable jobs that pair SQL templates with target
 *      database connections, supporting both on-demand and scheduled execution
 *      for recurring UAR data extraction workflows.
 */
const SqlJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedJob, setSelectedJob] = useState(null);
  const [executing, setExecuting] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    job_code: '',
    job_name: '',
    description: '',
    template_id: '',
    target_type: 'BY_TECHNOLOGY',
    target_connections: [],
    placeholder_values: {},
    execution_mode: 'SEQUENTIAL',
    max_parallel: 5,
    stop_on_error: false,
    schedule_type: 'MANUAL',
    schedule_cron: '',
    notify_on_completion: false,
    notify_on_failure: true,
    notification_emails: [],
  });
  const [saving, setSaving] = useState(false);

  const targetTypes = [
    { value: 'BY_TECHNOLOGY', label: 'All connections of template technology' },
    { value: 'SPECIFIC', label: 'Specific connections' },
    { value: 'BY_FILTER', label: 'Filter by criteria' },
    { value: 'ALL', label: 'All connections' },
  ];

  const executionModes = [
    { value: 'SEQUENTIAL', label: 'Sequential (one at a time)' },
    { value: 'PARALLEL', label: 'Parallel (concurrent)' },
  ];

  const scheduleTypes = [
    { value: 'MANUAL', label: 'Manual only' },
    { value: 'SCHEDULED', label: 'Scheduled (cron)' },
    { value: 'ON_DEMAND', label: 'On demand via API' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobsRes, templatesRes] = await Promise.all([
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/jobs/`),
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/templates/?status=active&approval=APPROVED`),
      ]);
      setJobs(jobsRes.data.jobs || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateNew = () => {
    setFormData({
      job_code: '',
      job_name: '',
      description: '',
      template_id: templates.length > 0 ? templates[0].template_id : '',
      target_type: 'BY_TECHNOLOGY',
      target_connections: [],
      placeholder_values: {},
      execution_mode: 'SEQUENTIAL',
      max_parallel: 5,
      stop_on_error: false,
      schedule_type: 'MANUAL',
      schedule_cron: '',
      notify_on_completion: false,
      notify_on_failure: true,
      notification_emails: [],
    });
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = async (job) => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/custom/jobs/${job.job_id}/`);
      setSelectedJob(response.data);
      setFormData({
        job_code: response.data.job_code,
        job_name: response.data.job_name,
        description: response.data.description || '',
        template_id: response.data.template_id,
        target_type: response.data.target_type,
        target_connections: response.data.target_connections || [],
        placeholder_values: response.data.placeholder_values || {},
        execution_mode: response.data.execution_mode,
        max_parallel: response.data.max_parallel,
        stop_on_error: response.data.stop_on_error,
        schedule_type: response.data.schedule_type,
        schedule_cron: response.data.schedule_cron || '',
        notify_on_completion: response.data.notify_on_completion,
        notify_on_failure: response.data.notify_on_failure,
        notification_emails: response.data.notification_emails || [],
      });
      setModalMode('edit');
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching job details:', err);
      alert('Failed to load job details');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        template_id: parseInt(formData.template_id),
      };

      if (modalMode === 'create') {
        await axios.post(`${API_CONFIG.BASE_URL}/api/custom/jobs/`, payload);
      } else {
        await axios.put(`${API_CONFIG.BASE_URL}/api/custom/jobs/${selectedJob.job_id}/`, payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving job:', err);
      alert(err.response?.data?.error || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleExecute = async (jobId) => {
    if (!window.confirm('Are you sure you want to execute this job now?')) return;

    setExecuting(jobId);
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/custom/jobs/${jobId}/execute/`
      );
      alert(`Execution started! ID: ${response.data.execution_id}`);
      fetchData();
    } catch (err) {
      console.error('Error executing job:', err);
      alert(err.response?.data?.error || 'Failed to execute job');
    } finally {
      setExecuting(null);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to deactivate this job?')) return;
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/api/custom/jobs/${jobId}/`);
      fetchData();
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      COMPLETED: 'bg-success',
      RUNNING: 'bg-primary',
      FAILED: 'bg-danger',
      PARTIAL: 'bg-warning text-dark',
    };
    return badges[status] || 'bg-secondary';
  };

  const getTechnologyBadge = (tech) => {
    const badges = {
      ORACLE: 'bg-danger',
      MSSQL: 'bg-primary',
      POSTGRESQL: 'bg-info',
      MYSQL: 'bg-warning text-dark',
      CASSANDRA: 'bg-success',
    };
    return badges[tech] || 'bg-secondary';
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">
            <i className="fas fa-tasks me-2"></i>
            Execution Jobs
          </h4>
          <p className="text-muted mb-0">Configure and run SQL templates against database connections</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateNew} disabled={templates.length === 0}>
          <i className="fas fa-plus me-2"></i>
          New Job
        </button>
      </div>

      {/* Warning if no templates */}
      {templates.length === 0 && !loading && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No approved templates available. Please create and approve a template first.
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Jobs Table */}
      <div className="card">
        <div className="card-header bg-secondary text-white">
          <h5 className="mb-0">
            <i className="fas fa-list me-2"></i>
            Jobs ({jobs.length})
          </h5>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : jobs.length > 0 ? (
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Job</th>
                  <th>Template</th>
                  <th className="text-center">Target</th>
                  <th className="text-center">Mode</th>
                  <th className="text-center">Schedule</th>
                  <th className="text-center">Last Run</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.job_id} className={!job.is_active ? 'table-secondary' : ''}>
                    <td>
                      <strong>{job.job_code}</strong>
                      <br />
                      <small className="text-muted">{job.job_name}</small>
                    </td>
                    <td>
                      <span className={`badge ${getTechnologyBadge(job.technology)} me-1`}>
                        {job.technology}
                      </span>
                      <br />
                      <small>{job.template_code}</small>
                    </td>
                    <td className="text-center">
                      <small>{job.target_type}</small>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${job.execution_mode === 'PARALLEL' ? 'bg-info' : 'bg-secondary'}`}>
                        {job.execution_mode}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${job.schedule_type === 'SCHEDULED' ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
                        {job.schedule_type}
                      </span>
                      {job.schedule_cron && (
                        <>
                          <br />
                          <small className="text-muted">{job.schedule_cron}</small>
                        </>
                      )}
                    </td>
                    <td className="text-center">
                      {job.last_run_at ? (
                        <>
                          <span className={`badge ${getStatusBadge(job.last_run_status)}`}>
                            {job.last_run_status}
                          </span>
                          <br />
                          <small className="text-muted">
                            {new Date(job.last_run_at).toLocaleDateString()}
                          </small>
                        </>
                      ) : (
                        <small className="text-muted">Never</small>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-success"
                          onClick={() => handleExecute(job.job_id)}
                          disabled={executing === job.job_id || !job.is_active}
                          title="Execute Now"
                        >
                          {executing === job.job_id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="fas fa-play"></i>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleEdit(job)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(job.job_id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-tasks fa-3x mb-3 opacity-25"></i>
              <p>No jobs found</p>
              {templates.length > 0 && (
                <button className="btn btn-primary" onClick={handleCreateNew}>
                  <i className="fas fa-plus me-2"></i>
                  Create Your First Job
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Job Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto',
          }}
        >
          <div style={{ padding: '20px' }}>
            <div
              className="modal-content"
              style={{ maxWidth: '900px', width: '100%', margin: '0 auto' }}
            >
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className={`fas ${modalMode === 'create' ? 'fa-plus' : 'fa-edit'} me-2`}></i>
                  {modalMode === 'create' ? 'New Execution Job' : 'Edit Job'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  {/* Left column */}
                  <div className="col-md-6">
                    <h6 className="text-muted mb-3">Basic Information</h6>

                    <div className="mb-3">
                      <label className="form-label">Job Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.job_code}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            job_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                          })
                        }
                        disabled={modalMode === 'edit'}
                        placeholder="e.g., ORACLE_UAR_DAILY"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Job Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.job_name}
                        onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                        placeholder="Human-readable name"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Template *</label>
                      <select
                        className="form-select"
                        value={formData.template_id}
                        onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                      >
                        <option value="">Select a template...</option>
                        {templates.map((t) => (
                          <option key={t.template_id} value={t.template_id}>
                            [{t.technology}] {t.template_code} - {t.template_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="What does this job do?"
                      ></textarea>
                    </div>

                    <h6 className="text-muted mb-3 mt-4">Target Connections</h6>

                    <div className="mb-3">
                      <label className="form-label">Target Type</label>
                      <select
                        className="form-select"
                        value={formData.target_type}
                        onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
                      >
                        {targetTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <small className="text-muted">
                        BY_TECHNOLOGY will run against all connections matching the template's database type
                      </small>
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="col-md-6">
                    <h6 className="text-muted mb-3">Execution Settings</h6>

                    <div className="mb-3">
                      <label className="form-label">Execution Mode</label>
                      <select
                        className="form-select"
                        value={formData.execution_mode}
                        onChange={(e) => setFormData({ ...formData, execution_mode: e.target.value })}
                      >
                        {executionModes.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>

                    {formData.execution_mode === 'PARALLEL' && (
                      <div className="mb-3">
                        <label className="form-label">Max Parallel Connections</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.max_parallel}
                          onChange={(e) =>
                            setFormData({ ...formData, max_parallel: parseInt(e.target.value) || 5 })
                          }
                          min="1"
                          max="20"
                        />
                      </div>
                    )}

                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="stopOnError"
                        checked={formData.stop_on_error}
                        onChange={(e) => setFormData({ ...formData, stop_on_error: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="stopOnError">
                        Stop execution on first error
                      </label>
                    </div>

                    <h6 className="text-muted mb-3 mt-4">Schedule</h6>

                    <div className="mb-3">
                      <label className="form-label">Schedule Type</label>
                      <select
                        className="form-select"
                        value={formData.schedule_type}
                        onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                      >
                        {scheduleTypes.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>

                    {formData.schedule_type === 'SCHEDULED' && (
                      <div className="mb-3">
                        <label className="form-label">Cron Expression</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.schedule_cron}
                          onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
                          placeholder="0 0 * * * (daily at midnight)"
                        />
                        <small className="text-muted">
                          Format: minute hour day month weekday
                        </small>
                      </div>
                    )}

                    <h6 className="text-muted mb-3 mt-4">Notifications</h6>

                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="notifyComplete"
                        checked={formData.notify_on_completion}
                        onChange={(e) =>
                          setFormData({ ...formData, notify_on_completion: e.target.checked })
                        }
                      />
                      <label className="form-check-label" htmlFor="notifyComplete">
                        Notify on completion
                      </label>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="notifyFailure"
                        checked={formData.notify_on_failure}
                        onChange={(e) =>
                          setFormData({ ...formData, notify_on_failure: e.target.checked })
                        }
                      />
                      <label className="form-check-label" htmlFor="notifyFailure">
                        Notify on failure
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving || !formData.job_code || !formData.job_name || !formData.template_id}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Job
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SqlJobs;
